/**
 * Inputs: current place fields + a maps-link hint. Outputs: filled address/name/pin without overwriting owner text.
 */

import type { Place } from "./schema.ts";
import { fillIfEmpty, MAPS_URL_COORD_SOURCE, type MapsPlaceHint } from "./maps-url.ts";

export function applyMapsHintToPlace(place: Place, hint: MapsPlaceHint, asOf: string): Place {
  const address = place.address?.trim() ? place.address : hint.address;
  const thaiName = place.thaiName?.trim() ? place.thaiName : hint.thaiName;
  const mapsUrl = place.mapsUrl?.trim() ? place.mapsUrl : hint.mapsUrl;
  const coordinates = place.coordinates ?? (hint.coordinates
    ? {
        lat: hint.coordinates.lat,
        lng: hint.coordinates.lng,
        verified: false,
        source: MAPS_URL_COORD_SOURCE,
        asOf,
      }
    : null);
  const extracted = Boolean(address || hint.coordinates);
  return {
    ...place,
    address: address?.trim() || null,
    thaiName: thaiName?.trim() || null,
    mapsUrl: mapsUrl?.trim() || null,
    coordinates,
    enrichmentStatus: extracted ? "maps_url_extracted" : place.enrichmentStatus,
  };
}

export type PlaceDraftFields = {
  name: string;
  mapsUrl: string;
  address: string;
  thaiName: string;
  coordinates: MapsPlaceHint["coordinates"];
  lookupStatus: "idle" | "reading" | "ready" | "partial" | "invalid";
  lookupNote: string | null;
};

export function emptyPlaceDraft(): PlaceDraftFields {
  return {
    name: "",
    mapsUrl: "",
    address: "",
    thaiName: "",
    coordinates: null,
    lookupStatus: "idle",
    lookupNote: null,
  };
}

export function applyMapsHintToDraft(current: PlaceDraftFields, hint: MapsPlaceHint): PlaceDraftFields {
  return {
    ...current,
    name: fillIfEmpty(current.name, hint.name),
    address: fillIfEmpty(current.address, hint.address),
    thaiName: fillIfEmpty(current.thaiName, hint.thaiName),
    coordinates: current.coordinates ?? hint.coordinates,
    lookupStatus: hint.address ? "ready" : "partial",
    lookupNote: hint.note,
  };
}

export function newPlaceFromMapsDraft(
  id: string,
  draft: { name: string; mapsUrl: string; address: string; thaiName: string; coordinates: MapsPlaceHint["coordinates"] },
  asOf: string,
): Place {
  const hint: MapsPlaceHint = {
    mapsUrl: draft.mapsUrl.trim(),
    name: draft.name.trim() || null,
    address: draft.address.trim() || null,
    thaiName: draft.thaiName.trim() || null,
    coordinates: draft.coordinates,
    addressSource: draft.address.trim() ? "maps_url" : null,
    note: null,
  };
  return applyMapsHintToPlace({
    id,
    name: draft.name.trim(),
    address: null,
    thaiName: null,
    mapsUrl: draft.mapsUrl.trim() || null,
    coordinates: null,
    sourceIds: [],
  }, hint, asOf);
}
